import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { usePageMeta } from "@/hooks/usePageMeta";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cashmyproperty.ai/" },
    { "@type": "ListItem", position: 2, name: "Services", item: "https://cashmyproperty.ai/services" },
    { "@type": "ListItem", position: 3, name: "Valuation", item: "https://cashmyproperty.ai/services/valuation" },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How is your valuation different from a bank valuation?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Bank valuations are conservative — they value only the current use and ignore development potential. Our valuation models future scenarios: what your land could be worth if redeveloped, if leased commercially, or if put into a JV. We factor in FSI optimization, upcoming infrastructure, and demand trends. Clients typically discover their land is worth 30–60% more than the bank's figure.",
      },
    },
    {
      "@type": "Question",
      name: "What data points do you use to determine value?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Our AI analyzes 50+ data points including recent comparable sales within a 5 km radius, upcoming infrastructure projects (metro, highways, airports), zoning and FSI regulations, demand heat maps, buyer profile data, rental yield benchmarks, and development cost models. The result is a valuation that reflects not just what your land is worth today — but what it could be worth with the right strategy.",
      },
    },
    {
      "@type": "Question",
      name: "Can I use your valuation report for a bank loan?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Our reports meet bank-grade standards and are accepted by all major Indian banks including SBI, HDFC, ICICI, and Axis for loan against property (LAP), mortgage loans, and project financing. The report includes IBBI-compliant registered valuer certification where required.",
      },
    },
    {
      "@type": "Question",
      name: "How long does the valuation process take?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Standard valuation takes 5–7 working days from document submission to final report delivery. Rush service is available at 48 hours for an additional fee. The process includes document review, site inspection, comparable analysis, DCF modeling, and peer review before the report reaches you.",
      },
    },
  ],
};

const steps = [
  {
    step: "01",
    title: "Document Collection & Review",
    desc: "We collect your title deed, encumbrance certificate, tax receipts, survey sketch, and any existing approvals. Our team verifies document authenticity and identifies any title gaps or compliance issues upfront.",
    icon: "ri-file-text-line",
  },
  {
    step: "02",
    title: "Physical Site Inspection",
    desc: "A certified valuer visits your property — measuring dimensions, recording GPS coordinates, assessing road access, topography, surrounding development, and neighborhood profile. 360-degree photographic documentation included.",
    icon: "ri-map-pin-line",
  },
  {
    step: "03",
    title: "AI-Powered Comparable Analysis",
    desc: "Our engine scans recent transactions within a 5 km radius, adjusts for plot size, road frontage, and corner premium, then benchmarks your property. We additionally factor in infrastructure announcements that may not yet be reflected in market prices.",
    icon: "ri-cpu-line",
  },
  {
    step: "04",
    title: "DCF & Development Potential Modeling",
    desc: "For commercial and development land, we build Discounted Cash Flow models projecting revenue under multiple scenarios — outright sale, JDA, leasing, and redevelopment. You see not one number, but a range with probability weights.",
    icon: "ri-line-chart-line",
  },
  {
    step: "05",
    title: "Report Delivery & Consultation",
    desc: "Your comprehensive valuation report is delivered with an executive summary, detailed methodology, comparable evidence, and strategic recommendations. A 30-minute consultation call with the valuer is included to walk you through the findings.",
    icon: "ri-file-list-3-line",
  },
];

const benefits = [
  { icon: "ri-shield-check-line", title: "Bank-Grade Accuracy", desc: "Reports meet IBBI and banking standards. Accepted by SBI, HDFC, ICICI, and all major lenders for mortgage and loan-against-property applications." },
  { icon: "ri-lightbulb-line", title: "Development Potential Upside", desc: "We don't just tell you what it's worth today — we show you what it could be worth under each monetization strategy. Most clients discover 30–60% hidden value." },
  { icon: "ri-radar-line", title: "Infrastructure Impact Analysis", desc: "Upcoming metro stations, highway expansions, airport projects, and SEZ announcements within 5 km are factored into your valuation — often revealing value not yet priced by the market." },
  { icon: "ri-line-chart-line", title: "Multi-Scenario Modeling", desc: "You receive three valuation figures: current market value, development potential value, and distressed sale value. Complete picture, no surprises." },
  { icon: "ri-file-shield-2-line", title: "Litigation-Ready Reports", desc: "Our reports include photographic evidence, comparable transaction data, and certified valuer signatures — admissible in court for partition suits, acquisition compensation, and family settlements." },
  { icon: "ri-speed-mini-line", title: "5–7 Day Turnaround", desc: "Standard delivery in 5–7 working days. Rush service available at 48 hours. No months-long wait like traditional valuers." },
];

const faqs = [
  { q: "How is your valuation different from a bank valuation?", a: "Bank valuations are conservative — they value only the current use and ignore development potential. Our valuation models future scenarios: what your land could be worth if redeveloped, if leased commercially, or if put into a JV. We factor in FSI optimization, upcoming infrastructure, and demand trends. Clients typically discover their land is worth 30–60% more than the bank's figure." },
  { q: "What data points do you use to determine value?", a: "Our AI analyzes 50+ data points including recent comparable sales within a 5 km radius, upcoming infrastructure projects (metro, highways, airports), zoning and FSI regulations, demand heat maps, buyer profile data, rental yield benchmarks, and development cost models. The result is a valuation that reflects not just what your land is worth today — but what it could be worth with the right strategy." },
  { q: "Can I use your valuation report for a bank loan?", a: "Yes. Our reports meet bank-grade standards and are accepted by all major Indian banks including SBI, HDFC, ICICI, and Axis for loan against property (LAP), mortgage loans, and project financing. The report includes IBBI-compliant registered valuer certification where required." },
  { q: "How long does the valuation process take?", a: "Standard valuation takes 5–7 working days from document submission to final report delivery. Rush service is available at 48 hours for an additional fee. The process includes document review, site inspection, comparable analysis, DCF modeling, and peer review before the report reaches you." },
];

export default function ValuationPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  usePageMeta({
    title: "Property Valuation — Bank-Grade Reports with Development Potential | CashMyProperty.ai",
    description: "Know what your land is really worth — not just today, but under every monetization strategy. AI-powered valuation with 50+ data points, comparable analysis, DCF modeling, and infrastructure impact assessment. Bank-grade reports accepted by all major lenders. 5–7 day turnaround.",
    canonicalUrl: "https://cashmyproperty.ai/services/valuation",
  });

  return (
    <PageLayout>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* Hero */}
      <section className="relative w-full min-h-[480px] md:min-h-[520px] overflow-hidden bg-background-50">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20real%20estate%20valuer%20examining%20property%20documents%20and%20architectural%20plans%20on%20desk%2C%20calculator%20and%20laptop%20visible%2C%20warm%20amber%20office%20lighting%2C%20clean%20modern%20aesthetic%2C%20financial%20analysis%20mood%2C%20India%20real%20estate%20valuation%2C%20high%20detail%20photography%2C%20deep%20teal%20and%20gold%20color%20palette%2C%20editorial%20composition&width=1800&height=800&seq=service-valuation-hero&orientation=landscape"
          alt="Property Valuation — CashMyProperty.ai"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-background-50/40" />
        <div className="relative h-full flex items-center container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 mb-5">
              <span className="text-white text-xs font-medium uppercase tracking-widest">Service Detail</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-[1.1] mb-4">
              <span className="text-white [text-shadow:0_0_24px_rgba(0,0,0,0.92)]">Property</span>
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-transparent drop-shadow-[0_0_16px_rgba(0,0,0,0.78)]">Valuation</span>
            </h1>
            <p className="text-lg text-white/90 leading-relaxed max-w-lg [text-shadow:0_0_16px_rgba(0,0,0,0.84)]">
              Bank-grade valuation reports that reveal the true development potential of your property — not just today's market price.
            </p>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="py-4 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-4-line mr-1" />Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/services" className="hover:text-primary-500 transition-colors whitespace-nowrap">Services</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">Valuation</span>
          </nav>
        </div>
      </section>

      {/* What is Valuation */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Know Your Land's True Worth</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
                Most Landowners Undervalue Their Property by 30–60%
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Traditional valuations look at what your land is worth today as vacant land. They ignore what it could be worth if developed, leased, or redeveloped. That is why most landowners leave crores on the table — they price their land based on an incomplete picture.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Our valuation engine runs <strong className="text-foreground-950">multiple development scenarios</strong> simultaneously — outright sale, JDA, commercial leasing, and redevelopment. Each scenario is modeled with 50+ data points including infrastructure impact, FSI optimization, and demand forecasting.
              </p>
              <p className="text-foreground-600 leading-relaxed">
                The result is not one number but a <strong className="text-foreground-950">value spectrum</strong> with strategic recommendations. You do not just know what your land is worth — you know exactly which path unlocks the maximum value.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Financial%20analyst%20reviewing%20property%20valuation%20charts%20and%20graphs%20on%20dual%20monitors%2C%20data%20visualization%20dashboard%2C%20warm%20amber%20desk%20lighting%2C%20clean%20modern%20professional%20workspace%2C%20real%20estate%20analytics%2C%20high%20detail%20photography%2C%20teal%20and%20gold%20tones%2C%20India%20corporate%20setting&width=800&height=600&seq=valuation-dashboard-01&orientation=landscape"
                alt="Property Valuation Dashboard"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Process</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Our 5-Step Valuation Process</h2>
            <p className="text-foreground-600">Rigorous. Data-driven. Delivered in 5–7 days.</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div key={step.step} className="flex gap-6 pb-10 relative">
                {i < steps.length - 1 && <div className="absolute left-[26px] top-14 bottom-0 w-px bg-primary-900/20" />}
                <div className="w-[52px] h-[52px] flex items-center justify-center rounded-full bg-primary-500/10 border-2 border-primary-500/30 shrink-0 z-10">
                  <span className="text-sm font-display font-bold text-primary-500">{step.step}</span>
                </div>
                <div className="pt-2">
                  <div className="flex items-center gap-3 mb-2">
                    <i className={`${step.icon} text-xl text-primary-500`} />
                    <h3 className="font-display font-semibold text-lg text-foreground-950">{step.title}</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Valuation Scenarios */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">What You Get</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Three Value Scenarios, One Report</h2>
            <p className="text-foreground-600">Every valuation includes all three scenarios so you can make an informed decision.</p>
          </div>
          <div className="grid sm:grid-cols-1 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: "ri-money-rupee-circle-line", title: "Current Market Value", desc: "What your land would sell for today in an arm's-length transaction. Based on 10+ comparable sales, adjusted for size, frontage, corner premium, and location factors.", color: "bg-primary-500/10 text-primary-700", badge: "Base Scenario" },
              { icon: "ri-line-chart-line", title: "Development Potential Value", desc: "What your land is worth if monetized optimally — JDA, leasing, redevelopment, or plotted development. Includes DCF models with revenue projections for each strategy.", color: "bg-accent-500/10 text-accent-700", badge: "Upside Scenario" },
              { icon: "ri-error-warning-line", title: "Distressed Sale Value", desc: "The liquidation floor — what you would get in a 30-day forced sale. Critical for risk planning, partnership exits, and loan collateral assessment.", color: "bg-secondary-500/10 text-secondary-700", badge: "Risk Scenario" },
            ].map((sc) => (
              <div key={sc.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300 relative overflow-hidden">
                <div className="absolute top-0 right-0">
                  <span className={`inline-block text-[10px] font-semibold px-3 py-1 rounded-bl-lg ${sc.color}`}>{sc.badge}</span>
                </div>
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4 mt-2">
                  <i className={`${sc.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{sc.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{sc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Why Our Valuation</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">What Sets Our Reports Apart</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((b) => (
              <div key={b.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${b.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{b.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-background-200/50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
            {[
              { value: "50+", label: "Data Points Analyzed" },
              { value: "30–60%", label: "Hidden Value Discovered" },
              { value: "5–7 Days", label: "Report Turnaround" },
              { value: "1,500+", label: "Valuations Completed" },
            ].map((stat) => (
              <div key={stat.label} className="p-5">
                <div className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-1">{stat.value}</div>
                <div className="text-xs text-foreground-500 font-medium uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold text-foreground-950">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`rounded-xl border transition-all duration-300 ${openFaq === i ? "bg-background-200 border-primary-700/40" : "card-dark"}`}>
                <button className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-display font-semibold text-foreground-950 pr-4">{faq.q}</span>
                  <i className={`ri-add-line text-primary-500 text-xl shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-6 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            Discover What Your Land is Really Worth
          </h2>
          <p className="text-foreground-600 mb-8">
            Most landowners are sitting on 30–60% more value than they realize. Find out in 5–7 days.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/feasibility-report" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              Get Valuation Report — ₹4,999
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary-700/50 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap">
              Talk to a Valuer
              <i className="ri-message-3-line" />
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}